import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  Mail, 
  Users, 
  User, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Eye,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Tent,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const pageSize = 20;

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/email-logs`, {
        params: { limit: pageSize, skip: page * pageSize },
      });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Napaka pri nalaganju zgodovine');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('sl-SI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeLabel = (type) => {
    const types = {
      'camp_mass': 'Masovno pošiljanje',
      'camp_single': 'Posamezniku',
      'camp_registration': 'Prijava na tabor',
      'test': 'Testni email',
    };
    return types[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      'camp_mass': 'bg-blue-700/30 text-blue-300 border-blue-600/40',
      'camp_single': 'bg-purple-700/30 text-purple-300 border-purple-600/40',
      'camp_registration': 'bg-emerald-700/30 text-emerald-300 border-emerald-600/40',
      'test': 'bg-amber-700/30 text-amber-300 border-amber-600/40',
    };
    return colors[type] || 'bg-stone-700/30 text-stone-300 border-stone-600/40';
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold text-white">Zgodovina pošiljanja</h2>
          <span className="px-2 py-1 text-xs bg-stone-800 text-stone-400 rounded-full">
            {total} emailov
          </span>
        </div>
      </div>

      {/* Logs Table */}
      {logs.length === 0 ? (
        <Card className="bg-stone-800/50 border-stone-700/50 p-8 text-center">
          <Mail className="w-12 h-12 text-stone-600 mx-auto mb-3" />
          <p className="text-stone-400">Še ni poslanih emailov.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-stone-400 border-b border-stone-700">
                <th className="text-left py-3 px-3">Datum</th>
                <th className="text-left py-3 px-3">Tip</th>
                <th className="text-left py-3 px-3">Zadeva</th>
                <th className="text-left py-3 px-3">Prejemniki</th>
                <th className="text-left py-3 px-3">Status</th>
                <th className="text-left py-3 px-3">Pošiljatelj</th>
                <th className="text-right py-3 px-3">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr 
                  key={log.id} 
                  className="border-b border-stone-800 hover:bg-stone-800/40 transition-colors"
                >
                  <td className="py-3 px-3 text-stone-400">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {formatDate(log.created_at)}
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <Badge className={`${getTypeColor(log.type)} border`}>
                      {log.type === 'camp_mass' ? <Users className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                      {getTypeLabel(log.type)}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 text-white max-w-xs truncate" title={log.subject}>
                    {log.subject}
                  </td>
                  <td className="py-3 px-3 text-stone-300">
                    {log.recipient_count === 1 ? (
                      <span>{log.recipients?.[0]?.email}</span>
                    ) : (
                      <span>{log.recipient_count} prejemnikov</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      {log.sent_count > 0 && (
                        <span className="flex items-center gap-1 text-emerald-400">
                          <CheckCircle className="w-4 h-4" />
                          {log.sent_count}
                        </span>
                      )}
                      {log.failed_count > 0 && (
                        <span className="flex items-center gap-1 text-red-400">
                          <XCircle className="w-4 h-4" />
                          {log.failed_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-stone-400">
                    {log.sent_by}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedLog(log)}
                      className="border-stone-700 text-stone-300 hover:bg-stone-800"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-stone-800">
          <p className="text-sm text-stone-400">
            Stran {page + 1} od {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-stone-700 text-stone-300 hover:bg-stone-800 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Nazaj
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="border-stone-700 text-stone-300 hover:bg-stone-800 disabled:opacity-50"
            >
              Naprej
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl bg-stone-900 border-stone-700 max-h-[90vh] overflow-y-auto">
          {selectedLog && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-emerald-400" />
                  Podrobnosti emaila
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-stone-400 text-sm">Datum</p>
                    <p className="text-white">{formatDate(selectedLog.created_at)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-stone-400 text-sm">Tip</p>
                    <Badge className={`${getTypeColor(selectedLog.type)} border`}>
                      {getTypeLabel(selectedLog.type)}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-stone-400 text-sm">Pošiljatelj</p>
                    <p className="text-white">{selectedLog.sent_by}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-stone-400 text-sm">Status</p>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-emerald-400">
                        <CheckCircle className="w-4 h-4" />
                        {selectedLog.sent_count} poslano
                      </span>
                      {selectedLog.failed_count > 0 && (
                        <span className="flex items-center gap-1 text-red-400">
                          <XCircle className="w-4 h-4" />
                          {selectedLog.failed_count} neuspelo
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Camp Info */}
                {selectedLog.camp_title && (
                  <div className="bg-stone-800/50 rounded-lg p-3 border border-stone-700">
                    <div className="flex items-center gap-2 text-emerald-400 mb-1">
                      <Tent className="w-4 h-4" />
                      <span className="text-sm font-medium">Povezan tabor</span>
                    </div>
                    <p className="text-white">{selectedLog.camp_title}</p>
                  </div>
                )}

                {/* Subject */}
                <div className="space-y-1">
                  <p className="text-stone-400 text-sm">Zadeva</p>
                  <p className="text-white font-medium">{selectedLog.subject}</p>
                </div>

                {/* Content Preview */}
                {selectedLog.content_preview && (
                  <div className="space-y-1">
                    <p className="text-stone-400 text-sm">Predogled vsebine</p>
                    <div className="bg-stone-800/50 rounded-lg p-3 border border-stone-700 text-stone-300 text-sm font-mono">
                      {selectedLog.content_preview}...
                    </div>
                  </div>
                )}

                {/* Recipients */}
                <div className="space-y-2">
                  <p className="text-stone-400 text-sm">
                    Prejemniki ({selectedLog.recipient_count})
                  </p>
                  <div className="bg-stone-800/50 rounded-lg border border-stone-700 max-h-48 overflow-y-auto">
                    {selectedLog.recipients?.map((r, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between px-3 py-2 border-b border-stone-700 last:border-0"
                      >
                        <div>
                          <p className="text-white text-sm">{r.name || '(brez imena)'}</p>
                          <p className="text-stone-400 text-xs">{r.email}</p>
                        </div>
                        {r.status === 'sent' ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  onClick={() => setSelectedLog(null)}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  Zapri
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailLogs;
